import { sidebarCollapsedAtom } from '@/atoms/sidebar.atom';
import { userThemeAtom } from '@/atoms/user.atom';
import { useHotkeys } from '@tanstack/react-hotkeys';
import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

/**
 * Global hotkeys active on all authenticated pages.
 * These are navigation & app-chrome shortcuts that do NOT depend on
 * a specific project being open.
 */
export const useGlobalHotkeys = (onOpenShortcutsDialog: () => void, onOpenSearch: () => void) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [, setSidebarCollapsed] = useAtom(sidebarCollapsedAtom);
  const [theme, setTheme] = useAtom(userThemeAtom);

  const isAuthPage = location.pathname.startsWith('/auth');

  useHotkeys([
    // ── Navigation ──────────────────────────────────
    {
      hotkey: 'Mod+Shift+H',
      callback: () => navigate('/'),
      options: { preventDefault: true },
    },

    // ── App chrome ──────────────────────────────────
    {
      hotkey: 'Mod+B',
      callback: () => setSidebarCollapsed((prev) => !prev),
      options: { preventDefault: true },
    },
    {
      hotkey: 'Mod+Shift+D',
      callback: () => {
        const next = !theme || theme === 'LIGHT' ? 'DARK' : 'LIGHT';
        setTheme(next as any);
      },
      options: { preventDefault: true },
    },

    // ── Search ──────────────────────────────────────
    {
      hotkey: 'Mod+K',
      callback: () => {
        if (!isAuthPage) onOpenSearch();
      },
      options: { preventDefault: true },
    },

    // ── Help ────────────────────────────────────────
    {
      hotkey: 'Shift+?',
      callback: () => onOpenShortcutsDialog(),
      options: { preventDefault: true },
    },
  ]);
};

/**
 * Project-scoped hotkeys – only active on routes under /projects/:projectId
 */
export const useProjectHotkeys = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const toggleSearchParam = useCallback(
    (key: string) => {
      const next = new URLSearchParams(searchParams);
      const current = next.get(key) === 'true';
      next.set(key, current ? 'false' : 'true');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  useHotkeys(
    [
      // ── Layers panel ────────────────────────────────
      {
        hotkey: 'Mod+Shift+L',
        callback: () => toggleSearchParam('layers'),
        options: { preventDefault: true },
      },

      // ── Project sub-pages ─────────────────────────
      {
        hotkey: 'Mod+Shift+U',
        callback: () => {
          if (projectId) navigate(`/projects/${projectId}/upload`);
        },
        options: { preventDefault: true },
      },
      {
        hotkey: 'Mod+Shift+E',
        callback: () => {
          if (projectId) navigate(`/projects/${projectId}/edit`);
        },
        options: { preventDefault: true },
      },
    ],
    { enabled: !!projectId },
  );
};
